import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Scan, X, Bold, Italic, Underline, Strikethrough } from "lucide-react";
import RangeSlider from "react-range-slider-input";
import "react-range-slider-input/dist/style.css"; // if required
import "../../../App.css";

const ToggleBoxToolbar = ({
  questionId,
  isLeftSidebarOpen,
  leftSidebarWidth = 0,
  isRightSidebarOpen,
  windowWidth,
  selectedComponent,
  onComponentUpdate,
  onDelete,
  onDeselect,
}) => {
  const [isMobile, setIsMobile] = useState(windowWidth < 768);
  const [fontSizeInput, setFontSizeInput] = useState(
    String(selectedComponent?.fontSize ?? 14)
  );
  const [borderWidthInput, setBorderWidthInput] = useState(
    String(selectedComponent?.borderWidth ?? 2)
  );

  const fontFamilies = [
    "Arial, sans-serif",
    "'Courier New', monospace",
    "'Times New Roman', serif",
    "'Comic Sans MS', cursive",
    "'Roboto', sans-serif",
  ];

  useEffect(() => {
    setIsMobile(windowWidth < 768);
  }, [windowWidth]);

  useEffect(() => {
    setFontSizeInput(String(selectedComponent?.fontSize ?? 14));
  }, [selectedComponent?.fontSize]);

  useEffect(() => {
    setBorderWidthInput(String(selectedComponent?.borderWidth ?? 2));
  }, [selectedComponent?.borderWidth]);

  const calculatePosition = () => {
    if (windowWidth < 768) {
      return { left: "50%", transform: "translateX(-50%)" };
    }
    const leftSidebarOffset = isLeftSidebarOpen ? leftSidebarWidth / 2 : 0;
    const rightSidebarOffset = isRightSidebarOpen ? -32 : 0;
    const totalOffset = leftSidebarOffset + rightSidebarOffset;
    const leftOffset =
      totalOffset !== 0 ? `calc(50% + ${totalOffset}px)` : "50%";
    return { left: leftOffset, transform: "translateX(-50%)" };
  };

  const position = calculatePosition();

  // Generic onChange handler
  const handleChange = (field, value) => {
    onComponentUpdate(questionId, selectedComponent.id, {
      [field]: value,
    });
  };

  // Special handler for corner changes that resets the shape
  const handleCornerChange = (newCornerCount) => {
    onComponentUpdate(questionId, selectedComponent.id, {
      corners: newCornerCount,
      anchors: null, // Reset anchors to trigger regeneration
      handles: null, // Reset handles to trigger regeneration
    });
  };

  // Toggle a font style flag
  const toggleStyle = (key) => {
    onComponentUpdate(questionId, selectedComponent.id, {
      fontStyles: {
        ...(selectedComponent?.fontStyles || {}),
        [key]: !selectedComponent?.fontStyles?.[key],
      },
    });
  };

  // Helpers for numeric inputs
  const clampNumber = (value, min, max, fallback) => {
    const parsed = parseInt(String(value), 10);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(max, Math.max(min, parsed));
  };

  const commitFontSize = () => {
    if (!selectedComponent?.id) return;
    const clamped = clampNumber(fontSizeInput, 8, 48, 14);
    setFontSizeInput(String(clamped));
    if (selectedComponent?.fontSize !== clamped) {
      onComponentUpdate(questionId, selectedComponent.id, {
        fontSize: clamped,
      });
    }
  };

  const resetFontSizeInput = () => {
    setFontSizeInput(String(selectedComponent?.fontSize ?? 14));
  };

  const commitBorderWidth = () => {
    if (!selectedComponent?.id) return;
    const clamped = clampNumber(borderWidthInput, 2, 8, 2);
    setBorderWidthInput(String(clamped));
    if (selectedComponent?.borderWidth !== clamped) {
      onComponentUpdate(questionId, selectedComponent.id, {
        borderWidth: clamped,
      });
    }
  };

  const resetBorderWidthInput = () => {
    setBorderWidthInput(String(selectedComponent?.borderWidth ?? 2));
  };

  return (
    <div
      className="fixed z-50 transition-all duration-300"
      style={{
        left: position.left,
        transform: position.transform,
        bottom: isMobile ? "calc(34px + env(safe-area-inset-bottom, 0px))" : 34,
      }}
    >
      <motion.div
        key={questionId}
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className={`bg-white rounded-xl shadow-xl border border-gray-200 relative`}
        style={{
          width: isMobile ? "calc(100vw - 24px)" : "600px",
          maxWidth: "calc(100vw - 24px)",
          maxHeight: isMobile ? "40vh" : "none",
          overflowY: isMobile ? "auto" : "visible",
        }}
      >
        <button
          onClick={onDeselect}
          className={`${
            isMobile ? "top-2 right-2" : "-top-3 right-2"
          } absolute bg-white rounded-full ${
            isMobile ? "p-1" : "p-1.5"
          } shadow-md border border-gray-200`}
          aria-label="Close"
          title="Close"
          type="button"
        >
          <X size={isMobile ? 14 : 16} className="text-gray-700" />
        </button>

        {/* Delete button */}
        {/* {onDelete && (
          <button
            onClick={() => onDelete(selectedComponent?.id)}
            className="absolute -top-3 -right-3 bg-red-500 rounded-full p-1 shadow-md border border-gray-200 text-white hover:bg-red-600 transition-colors"
            aria-label="Delete component"
            type="button"
          >
            <X size={16} />
          </button>
        )} */}

        <div className={`${isMobile ? "px-2 py-3" : "px-3 py-3"} w-full`}>
          <div
            className="grid gap-3"
            style={{
              gridTemplateColumns: isMobile
                ? "repeat(auto-fit, minmax(160px, 1fr))"
                : "repeat(3, minmax(0, 1fr))",
            }}
          >
            {/* Column 1: Text Options */}
            <div className="flex flex-col gap-2">
              <p className="text-xs text-black font-semibold">Text Options</p>
              <div className="flex gap-1">
                <label className="flex-1">
                  <select
                    value={selectedComponent?.fontFamily || fontFamilies[0]}
                    onChange={(e) =>
                      onComponentUpdate(questionId, selectedComponent.id, {
                        fontFamily: e.target.value,
                      })
                    }
                    className="block w-full rounded-md border border-gray-300 p-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    {fontFamilies.map((font) => (
                      <option key={font} value={font}>
                        {font.split(",")[0]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="w-12 md:w-10">
                  <input
                    type="number"
                    min={8}
                    max={48}
                    value={fontSizeInput}
                    onChange={(e) => {
                      const next = e.target.value;
                      setFontSizeInput(next);
                      const parsed = parseInt(next, 10);
                      if (
                        Number.isFinite(parsed) &&
                        parsed >= 8 &&
                        parsed <= 48 &&
                        selectedComponent?.fontSize !== parsed
                      ) {
                        onComponentUpdate(questionId, selectedComponent.id, {
                          fontSize: parsed,
                        });
                      }
                    }}
                    onBlur={commitFontSize}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitFontSize();
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        resetFontSizeInput();
                      }
                    }}
                    className="block w-full rounded-md border border-gray-300 p-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </label>
              </div>
              <div className="flex gap-1 flex-wrap">
                {[
                  { key: "bold", icon: <Bold className="h-5 w-5" /> },
                  { key: "italic", icon: <Italic className="h-5 w-5" /> },
                  { key: "underline", icon: <Underline className="h-5 w-5" /> },
                  {
                    key: "lineThrough",
                    icon: <Strikethrough className="h-5 w-5" />,
                  },
                ].map(({ key, icon }) => {
                  const titleMap = {
                    bold: "Bold",
                    italic: "Italic",
                    underline: "Underline",
                    lineThrough: "Strikethrough",
                  };
                  const isActive = Boolean(
                    selectedComponent?.fontStyles?.[key]
                  );
                  return (
                    <button
                      key={key}
                      onClick={() => toggleStyle(key)}
                      className={`w-8 h-8 rounded border text-sm font-semibold flex items-center justify-center select-none ${
                        isActive
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                      }`}
                      title={titleMap[key]}
                      aria-pressed={isActive}
                      type="button"
                    >
                      {icon}
                    </button>
                  );
                })}
              </div>
              <label>
                <p className="text-xs text-black font-semibold mb-1">
                  Font Color
                </p>
                <div className="flex justify-center items-center w-full h-8 overflow-hidden rounded-md shadow">
                  <input
                    type="color"
                    value={selectedComponent?.fontColor || "#000000"}
                    onChange={(e) =>
                      onComponentUpdate(questionId, selectedComponent.id, {
                        fontColor: e.target.value,
                      })
                    }
                    className="w-[calc(100%+8px)] h-14 -m-1 border-none p-0 focus:outline-none ring-0 outline-none"
                  />
                </div>
              </label>
            </div>

            {/* Column 2: Colors + Opacity */}
            <div className="flex flex-col gap-3">
              {/* Background Color */}
              <label className="flex items-center gap-2">
                <div className="flex justify-center items-center w-10 h-10 overflow-hidden rounded-lg shadow-lg border border-gray-200">
                  <input
                    type="color"
                    value={selectedComponent?.backgroundColor ?? "#ffffff"}
                    onChange={(e) =>
                      handleChange("backgroundColor", e.target.value)
                    }
                    className="w-[calc(100%+8px)] h-14 -m-1 border-none p-0 focus:outline-none ring-0 outline-none"
                  />
                </div>
                <p className="text-xs text-black font-semibold mb-1">
                  Background
                  <br />
                  Color
                </p>
              </label>
              {/* Border Color */}
              <label className="flex items-center gap-2">
                <div className="flex justify-center items-center w-10 h-10 overflow-hidden rounded-lg shadow-lg border border-gray-200 ">
                  <input
                    type="color"
                    value={selectedComponent?.borderColor ?? "#000000"}
                    onChange={(e) =>
                      handleChange("borderColor", e.target.value)
                    }
                    className="w-[calc(100%+8px)] h-14 -m-1 border-none p-0 focus:outline-none ring-0 outline-none"
                  />
                </div>
                <p className="text-xs text-black font-semibold mb-1">
                  Border
                  <br />
                  Color
                </p>
              </label>
              {/* Opacity (moved here) */}
              <div className="flex flex-col gap-2 w-full">
                <p className="text-xs text-black font-semibold">Opacity</p>
                <div>
                  <RangeSlider
                    className="single-thumb"
                    min={0}
                    max={1}
                    step={0.01}
                    value={[0, selectedComponent?.opacity ?? 1]}
                    thumbsDisabled={[true, false]}
                    rangeSlideDisabled={true}
                    onInput={([_, newValue]) =>
                      handleChange("opacity", parseFloat(newValue.toFixed(2)))
                    }
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {(selectedComponent?.opacity ?? 1).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            {/* Column 3: Geometry */}
            <div className="flex flex-col gap-2">
              {/* Number of Corners */}
              <div className="flex flex-col gap-2">
                <p className="text-xs text-black font-semibold">
                  Number of Corners
                </p>
                <div className="min-w-36">
                  <RangeSlider
                    className="single-thumb"
                    min={3}
                    max={9}
                    step={1}
                    value={[3, selectedComponent?.corners ?? 4]}
                    onInput={([_, newValue]) =>
                      handleCornerChange(parseInt(newValue, 10))
                    }
                    thumbsDisabled={[true, false]}
                    rangeSlideDisabled={true}
                  />
                  <div className="text-xs text-gray-500 mt-1">
                    {selectedComponent?.corners ?? 4} corners (Min 3, Max 9)
                  </div>
                </div>
              </div>
              {/* Border width */}
              <label>
                <p className="text-xs text-black font-semibold mb-1">
                  Border Width
                </p>
                <div className="flex items-center gap-2 pr-1 pl-2 w-max h-8 overflow-hidden rounded-md bg-gray-200 shadow-md">
                  <Scan className="h-5 w-5" />
                  <input
                    type="number"
                    min={2}
                    max={8}
                    value={borderWidthInput}
                    onChange={(e) => {
                      const next = e.target.value;
                      setBorderWidthInput(next);
                      const parsed = parseInt(next, 10);
                      if (
                        Number.isFinite(parsed) &&
                        parsed >= 2 &&
                        parsed <= 8 &&
                        selectedComponent?.borderWidth !== parsed
                      ) {
                        onComponentUpdate(questionId, selectedComponent.id, {
                          borderWidth: parsed,
                        });
                      }
                    }}
                    onBlur={commitBorderWidth}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitBorderWidth();
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        resetBorderWidthInput();
                      }
                    }}
                    className="w-8 border-none text-[11px] font-medium bg-transparent focus:outline-none"
                  />
                </div>
              </label>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ToggleBoxToolbar;
